import { loadAppLanguageId, t } from "utils/appLanguage";

// Shared handle to the active TradingView widget so the full-height tool rail
// (rendered outside the chart iframe) can drive the chart's drawing tools.
//
// Which object owns what, per charting_library.d.ts:
//   IChartingLibraryWidget (the widget, from `new TradingView.widget`)
//     line 11747+ — selectLineTool, selectedLineTool, magnetEnabled,
//     hideAllDrawingTools, lockAllDrawingTools, takeScreenshot,
//     subscribe/unsubscribe
//   IChartWidgetApi (activeChart())
//     lines 10732-11746 — removeAllShapes, resolution, setResolution,
//     onIntervalChanged, executeActionById
// Calling selectLineTool on activeChart() is a silent no-op: it is not there.

let widget = null;
const listeners = new Set();

const DEBUG = process.env.NODE_ENV !== "production";

function log(...args) {
  if (DEBUG) console.warn("[chart-tools]", ...args);
}

export function setChartWidget(w) {
  widget = w;
  if (w) log("widget registered.", describeCapabilities());
  listeners.forEach((fn) => {
    try { fn(w); } catch {}
  });
}

/** Subscribers are called immediately with the current widget, if there is one,
 *  so a rail that mounts after the chart is ready still becomes usable. */
export function subscribeChartWidget(fn) {
  listeners.add(fn);
  if (widget) {
    try { fn(widget); } catch {}
  }
  return () => listeners.delete(fn);
}

/** Reports which pieces of the drawing API are actually present. */
export function describeCapabilities() {
  return {
    widget: !!widget,
    selectLineTool: typeof widget?.selectLineTool,
    selectedLineTool: typeof widget?.selectedLineTool,
    subscribe: typeof widget?.subscribe,
    activeChart: typeof widget?.activeChart,
    removeAllShapes: typeof activeChart()?.removeAllShapes,
    magnetEnabled: typeof widget?.magnetEnabled,
  };
}

export function isChartReady() {
  return typeof widget?.selectLineTool === "function";
}

/** Wait until TradingView activeChart().executeActionById is usable (chart remounts when Settings opens). */
export function waitForActiveChart(timeoutMs = 12000) {
  return new Promise((resolve) => {
    const get = () => {
      try {
        const c = widget?.activeChart?.();
        if (c && typeof c.executeActionById === "function") return c;
      } catch {
        /* not ready */
      }
      return null;
    };
    const hit = get();
    if (hit) {
      resolve(hit);
      return;
    }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      try {
        unsub();
      } catch {
        /* ignore */
      }
      clearInterval(poll);
      clearTimeout(timer);
      resolve(value);
    };
    const unsub = subscribeChartWidget(() => {
      const c = get();
      if (c) finish(c);
    });
    const poll = setInterval(() => {
      const c = get();
      if (c) finish(c);
    }, 50);
    const timer = setTimeout(() => finish(get()), timeoutMs);
  });
}

function activeChart() {
  try {
    return widget?.activeChart?.() ?? null;
  } catch {
    return null;
  }
}

/** selectLineTool is async — await it before reading the selection back.
 *  `options` carries the payload the emoji and icon tools need, e.g.
 *  selectTool("emoji", { emoji: "🚀" }); without it the tool falls back to the
 *  single default glyph from linetoolemoji.emoji. */
export async function selectTool(tool, options) {
  const w = widget;
  if (typeof w?.selectLineTool !== "function") {
    log(`selectLineTool unavailable for "${tool}".`, describeCapabilities());
    return false;
  }
  try {
    if (options) await w.selectLineTool(tool, options);
    else await w.selectLineTool(tool);
  } catch (err) {
    log(`selectLineTool("${tool}") threw:`, err);
    return false;
  }
  try {
    if (typeof w.selectedLineTool === "function") {
      const now = w.selectedLineTool();
      if (now !== tool) {
        log(`selectLineTool("${tool}") did not stick; chart reports "${now}".`);
        // The tool may still be armed, so treat this as success for the UI.
      }
      return true;
    }
  } catch {}
  return true;
}

export function currentTool() {
  try {
    return widget?.selectedLineTool?.() ?? null;
  } catch {
    return null;
  }
}

/** Fires whenever the active tool changes (e.g. reverts after a drawing ends). */
export function subscribeToolChange(fn) {
  const w = widget;
  if (typeof w?.subscribe !== "function") return () => {};
  try {
    w.subscribe("onSelectedLineToolChanged", fn);
  } catch {
    return () => {};
  }
  return () => {
    try { w.unsubscribe("onSelectedLineToolChanged", fn); } catch {}
  };
}

export function removeAllDrawings() {
  try {
    activeChart()?.removeAllShapes?.();
  } catch (err) {
    log("removeAllShapes failed:", err);
  }
}

// ── Interval + header actions ────────────────────────────────────────────
// The native chart header is disabled, so the app's top bar owns these.

export function currentResolution() {
  try {
    return activeChart()?.resolution?.() ?? null;
  } catch {
    return null;
  }
}

export async function setResolution(resolution) {
  const chart = activeChart();
  if (typeof chart?.setResolution !== "function") {
    log(`setResolution unavailable for "${resolution}".`);
    return false;
  }
  try {
    await chart.setResolution(resolution);
    return true;
  } catch (err) {
    log(`setResolution("${resolution}") failed:`, err);
    return false;
  }
}

/**
 * TradingView ChartStyle enum values, grouped like the library chart-type menu.
 * `sep: true` inserts a divider after that row (matches TV menu separators).
 */
export const CHART_STYLE_OPTIONS = [
  { id: 0, key: "ohlc", label: "Bars" },
  { id: 1, key: "candle", label: "Candles" },
  { id: 9, key: "hollow", label: "Hollow candles" },
  { id: 19, key: "volcandle", label: "Volume candles", sep: true },
  { id: 2, key: "line", label: "Line" },
  { id: 14, key: "linemarkers", label: "Line with markers" },
  { id: 15, key: "stepline", label: "Step line", sep: true },
  { id: 3, key: "area", label: "Area" },
  { id: 16, key: "hlcarea", label: "HLC area" },
  { id: 10, key: "baseline", label: "Baseline", sep: true },
  { id: 13, key: "column", label: "Columns" },
  { id: 12, key: "hilo", label: "High-low" },
];

export function chartStyleFromPref(key) {
  return CHART_STYLE_OPTIONS.find((t) => t.key === key)?.id ?? 1;
}

export function chartPrefFromStyle(id) {
  return CHART_STYLE_OPTIONS.find((t) => t.id === Number(id))?.key ?? "candle";
}

export function currentChartType() {
  try {
    const t = activeChart()?.chartType?.();
    return typeof t === "number" ? t : 1;
  } catch {
    return 1;
  }
}

export async function setChartType(type) {
  const chart = activeChart();
  if (typeof chart?.setChartType !== "function") {
    log(`setChartType unavailable for "${type}".`);
    return false;
  }
  try {
    await chart.setChartType(type);
    return true;
  } catch (err) {
    log(`setChartType(${type}) failed:`, err);
    return false;
  }
}

export function subscribeChartTypeChange(fn) {
  const sub = (() => {
    try {
      return activeChart()?.onChartTypeChanged?.() ?? null;
    } catch {
      return null;
    }
  })();
  if (!sub?.subscribe) return () => {};
  const handler = (chartType) => fn(chartType);
  try {
    sub.subscribe(null, handler);
  } catch {
    return () => {};
  }
  return () => {
    try { sub.unsubscribe(null, handler); } catch {}
  };
}

export function subscribeResolutionChange(fn) {
  const sub = (() => {
    try {
      return activeChart()?.onIntervalChanged?.() ?? null;
    } catch {
      return null;
    }
  })();
  if (!sub?.subscribe) return () => {};
  const handler = (interval) => fn(interval);
  try {
    sub.subscribe(null, handler);
  } catch {
    return () => {};
  }
  return () => {
    try { sub.unsubscribe(null, handler); } catch {}
  };
}

/** Built-in TradingView study names — same catalog as insertIndicator. */
export function getStudiesList() {
  try {
    const list = widget?.getStudiesList?.();
    if (!Array.isArray(list)) return [];
    return list
      .map((name) => String(name || "").trim())
      .filter(Boolean);
  } catch (err) {
    log("getStudiesList failed:", err);
    return [];
  }
}

function normalizeStudyColor(value) {
  const raw = String(value || "").trim();
  if (!raw || raw === "null" || raw === "undefined") return "#2196f3";
  if (raw.startsWith("#")) return raw.length === 4
    ? `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`
    : raw.slice(0, 7);
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw}`;
  if (/^[0-9a-fA-F]{3}$/.test(raw)) return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  return "#2196f3";
}

const HIDDEN_PLOT_TYPES = new Set([
  "colorer",
  "rgba_colorer",
  "data",
  "dataoffset",
  "ohlc_colorer",
  "bar_colorer",
  "bg_colorer",
  "text_colorer",
  "up_colorer",
  "down_colorer",
  "wick_colorer",
  "border_colorer",
  "candle_wick_colorer",
  "candle_border_colorer",
]);

const SKIP_INPUT_TYPES = new Set(["symbol", "session", "time", "bar_time", "text_area"]);

function defaultInputValue(inp) {
  if (inp.defval != null) return inp.defval;
  const type = String(inp.type || "").toLowerCase();
  if (type === "bool") return false;
  if (type === "source") return "close";
  if (type === "resolution") return "";
  if (type === "integer" || type === "float" || type === "price") {
    const n = String(inp.name || inp.id || "").toLowerCase();
    if (n.includes("period") || n.includes("length") || n.includes("smooth")) return 14;
    if (n.includes("shift")) return 0;
    if (n.includes("deviation") || n.includes("mult")) return 2;
    return type === "integer" ? 1 : 0;
  }
  return "";
}

/** Inputs + plot styles already on the TradingView study — nothing extra is invented. */
export function getStudyMeta(studyName) {
  const name = String(studyName || "").trim();
  if (!name) return { inputs: [], plots: [] };
  try {
    const inputsRaw = widget?.getStudyInputs?.(name) || [];
    const styles = widget?.getStudyStyles?.(name) || {};
    const inputs = (Array.isArray(inputsRaw) ? inputsRaw : [])
      .filter((inp) => inp && !inp.isHidden && !SKIP_INPUT_TYPES.has(String(inp.type || "").toLowerCase()))
      .map((inp) => ({
        id: inp.id,
        name: inp.localizedName || inp.name || inp.id,
        type: String(inp.type || "text").toLowerCase(),
        defval: defaultInputValue(inp),
        min: inp.min,
        max: inp.max,
        step: inp.step,
        options: inp.options,
        optionsTitles: inp.optionsTitles,
      }));

    const plotMeta = styles.styles || {};
    const plotDefaults = styles.defaults?.styles || {};
    const plots = (styles.plots || [])
      .filter((plot) => {
        if (!plot?.id) return false;
        if (HIDDEN_PLOT_TYPES.has(String(plot.type || "").toLowerCase())) return false;
        if (plotMeta[plot.id]?.isHidden) return false;
        return true;
      })
      .map((plot) => {
        const def = plotDefaults[plot.id] || {};
        return {
          id: plot.id,
          title: plotMeta[plot.id]?.title || plot.id,
          color: normalizeStudyColor(def.color),
          linewidth: Number(def.linewidth) > 0 ? Number(def.linewidth) : 1,
          visible: def.visible !== false && Number(def.display) !== 0,
        };
      });

    return { inputs, plots };
  } catch (err) {
    log(`getStudyMeta("${name}") failed:`, err);
    return { inputs: [], plots: [] };
  }
}

/** Add a study already in getStudiesList() using TradingView defaults (no extra indicators). */
export async function addStudy(name, options = {}) {
  const chart = activeChart();
  const studyName = String(name || "").trim();
  if (!studyName) return false;
  if (typeof chart?.createStudy !== "function") {
    log(`createStudy unavailable for "${studyName}".`, describeCapabilities());
    return false;
  }
  const inputs = options.inputs && Object.keys(options.inputs).length ? options.inputs : undefined;
  const overrides = options.overrides && Object.keys(options.overrides).length ? options.overrides : undefined;
  try {
    await chart.createStudy(studyName, undefined, false, inputs, overrides);
    return true;
  } catch (err) {
    log(`createStudy("${studyName}") failed:`, err);
    if (inputs || overrides) {
      try {
        await chart.createStudy(studyName);
        return true;
      } catch (fallbackErr) {
        log(`createStudy("${studyName}") fallback failed:`, fallbackErr);
      }
    }
    return false;
  }
}

/** Opens the library's own dialogs, e.g. "insertIndicator", "chartProperties", "paneObjectTree". */
export function executeAction(actionId) {
  const chart = activeChart();
  if (typeof chart?.executeActionById !== "function") {
    log(`executeActionById unavailable for "${actionId}".`, describeCapabilities());
    return false;
  }
  try {
    chart.executeActionById(actionId);
    return true;
  } catch (err) {
    log(`executeActionById("${actionId}") failed:`, err);
    return false;
  }
}

export function currentChartSymbol() {
  try {
    return String(
      activeChart()?.symbol?.() || widget?.symbol?.() || ""
    ).trim();
  } catch {
    return "";
  }
}

const DRAWING_LABELS = [
  [/gann.*fan/i, "Gann Fan"],
  [/gann.*(grid|square)/i, "Gann Grid"],
  [/gann/i, "Gann Line"],
  [/fib.*retrac|retracement/i, "Fibonacci Retracement"],
  [/fib.*time/i, "Fib Time Zones"],
  [/fib.*fan/i, "Fib Fan"],
  [/fib.*arc/i, "Fib Arcs"],
  [/regression/i, "Regression Channel"],
  [/parallel|equidistant/i, "Equidistant Channel"],
  [/std\s*dev|fib.?channel/i, "StdDev Channel"],
  [/pitchfork/i, "Andrews Pitchfork"],
  [/trend/i, "Trendline"],
  [/horiz/i, "Horizontal Line"],
  [/vert/i, "Vertical Line"],
  [/rect/i, "Rectangle"],
  [/circle/i, "Circle"],
  [/triangle/i, "Triangle"],
  [/ellipse/i, "Ellipse"],
  [/text/i, "Text"],
];

function friendlyDrawingName(raw) {
  const s = String(raw || "").replace(/^LineTool/i, "").replace(/[_-]+/g, " ").trim();
  if (!s) return "Drawing";
  for (const [re, label] of DRAWING_LABELS) {
    if (re.test(s)) return label;
  }
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function drawingKind(raw) {
  const s = String(raw || "").toLowerCase();
  if (/gann/.test(s) && /fan/.test(s)) return "gannFan";
  if (/gann/.test(s) && /(grid|square)/.test(s)) return "gannGrid";
  if (/gann/.test(s)) return "gann";
  if (/fib|retrac/.test(s)) return "fib";
  if (/regression|parallel|equidistant|channel|pitchfork/.test(s)) return "channel";
  if (/rect/.test(s)) return "rectangle";
  if (/text/.test(s)) return "text";
  return "trend";
}

/** Chart series + drawings for the MT5-style Manage Drawings panel. */
export function listManagedDrawings() {
  const chart = activeChart();
  const symbol = currentChartSymbol();
  const items = [];
  if (symbol) {
    items.push({ id: "__series__", kind: "series", name: symbol, selectable: false });
  }
  if (typeof chart?.getAllShapes !== "function") return items;
  try {
    for (const info of chart.getAllShapes() || []) {
      let raw = info?.name || "";
      try {
        const props = chart.getShapeById?.(info.id)?.getProperties?.();
        raw = props?.title || props?.text || raw;
      } catch {
        /* ignore */
      }
      let locked = false;
      let hidden = false;
      try {
        const shape = chart.getShapeById?.(info.id);
        const props = shape?.getProperties?.() || {};
        locked = props.frozen === true || shape?.isUserEditEnabled?.() === false;
        hidden = props.visible === false;
      } catch {
        /* ignore */
      }
      items.push({
        id: info.id,
        kind: drawingKind(raw || info?.name),
        name: friendlyDrawingName(raw || info?.name),
        selectable: true,
        locked,
        hidden,
      });
    }
  } catch (err) {
    log("listManagedDrawings failed:", err);
  }
  return items;
}

export function selectManagedDrawing(id) {
  const chart = activeChart();
  if (!chart || id == null || id === "__series__") return false;
  try {
    chart.selection?.()?.set?.(id);
    return true;
  } catch (err) {
    log("selectManagedDrawing failed:", err);
    return false;
  }
}

function drawingApi(id) {
  try {
    return activeChart()?.getShapeById?.(id) || null;
  } catch {
    return null;
  }
}

export function toggleDrawingLock(id) {
  const shape = drawingApi(id);
  if (!shape) return false;
  try {
    const props = shape.getProperties?.() || {};
    const locked = props.frozen === true || shape.isUserEditEnabled?.() === false;
    shape.setProperties?.({ frozen: !locked });
    shape.setUserEditEnabled?.(!locked ? false : true);
    return true;
  } catch (err) {
    log("toggleDrawingLock failed:", err);
    return false;
  }
}

export function toggleDrawingVisible(id) {
  const shape = drawingApi(id);
  if (!shape) return false;
  try {
    const props = shape.getProperties?.() || {};
    const visible = props.visible !== false;
    shape.setProperties?.({ visible: !visible });
    return true;
  } catch (err) {
    log("toggleDrawingVisible failed:", err);
    return false;
  }
}

export function openDrawingProperties(id) {
  const chart = activeChart();
  if (typeof chart?.showPropertiesDialog !== "function" || id == null) return false;
  try {
    chart.showPropertiesDialog(id);
    return true;
  } catch (err) {
    log("openDrawingProperties failed:", err);
    return false;
  }
}

export function removeManagedDrawing(id) {
  const chart = activeChart();
  if (typeof chart?.removeEntity !== "function" || id == null || id === "__series__") return false;
  try {
    try { chart.selection?.()?.clear?.(); } catch {}
    chart.removeEntity(id);
    try { widget?.selectLineTool?.("cursor"); } catch {}
    return true;
  } catch (err) {
    log("removeManagedDrawing failed:", err);
    return false;
  }
}

export function subscribeDrawingsChange(fn) {
  const w = widget;
  if (typeof w?.subscribe !== "function") return () => {};
  const handler = () => {
    try { fn(); } catch {}
  };
  try {
    w.subscribe("drawing_event", handler);
  } catch {
    return () => {};
  }
  return () => {
    try { w.unsubscribe("drawing_event", handler); } catch {}
  };
}

/** TradingView has no Hindi locale — overlay-translate Chart Settings labels. */
const TV_CHART_HI_INNER = {
  Last: "अंतिम",
  Close: "बंद",
  "Bid and ask": "बोली और मांग",
  "High and low": "उच्च और निम्न",
  "OHLC values": "OHLC मान",
  "Indicators and financials values": "संकेतक और वित्तीय मान",
  "Bar change values": "बार बदलाव मान",
  Price: "कीमत",
  "Labels on price scale": "मूल्य स्केल पर लेबल",
  Value: "मान",
  Values: "मान",
  "Previous close": "पिछला बंद",
  Precision: "परिशुद्धता",
  Timezone: "समय क्षेत्र",
  "Price line": "मूल्य रेखा",
  "Last price line": "अंतिम मूल्य रेखा",
  "Bid and ask labels": "बोली और मांग लेबल",
  "High and low price labels": "उच्च और निम्न मूल्य लेबल",
  "Average close price": "औसत बंद मूल्य",
  "Countdown to bar close": "बार बंद होने तक काउंटडाउन",
  "Currency and unit": "मुद्रा और इकाई",
  "Symbol name": "प्रतीक नाम",
  Description: "विवरण",
  Logo: "लोगो",
  "Open market status": "खुले बाज़ार की स्थिति",
  Background: "पृष्ठभूमि",
  "Text color": "टेक्स्ट रंग",
  "Font size": "फ़ॉन्ट आकार",
  "Bar change": "बार बदलाव",
  "Last day change": "पिछले दिन का बदलाव",
  "Indicator titles": "संकेतक शीर्षक",
  "Indicator values": "संकेतक मान",
  "Auto scale": "ऑटो स्केल",
  "Scale price chart only": "केवल मूल्य चार्ट स्केल करें",
  "Lock scale": "स्केल लॉक करें",
  Percentage: "प्रतिशत",
  Percent: "प्रतिशत",
  "Indexed to 100": "100 पर इंडेक्स",
  Logarithmic: "लघुगणकीय",
  "Invert scale": "स्केल उल्टा करें",
  Regular: "नियमित",
  Labels: "लेबल",
  Lines: "रेखाएँ",
  "No overlapping labels": "ओवरलैपिंग लेबल नहीं",
  "Plus button": "प्लस बटन",
  "Date format": "दिनांक प्रारूप",
  "Time hours format": "समय घंटे प्रारूप",
  "Scales placement": "स्केल स्थान",
  Left: "बाएँ",
  Right: "दाएँ",
  Auto: "ऑटो",
  Crosshair: "क्रॉसहेयर",
  "Vert grid lines": "लंबवत ग्रिड रेखाएँ",
  "Horz grid lines": "क्षैतिज ग्रिड रेखाएँ",
  "Top margin": "ऊपरी मार्जिन",
  "Bottom margin": "निचला मार्जिन",
  "Session breaks": "सत्र विराम",
  "Scales text": "स्केल टेक्स्ट",
  "Scales lines": "स्केल रेखाएँ",
  Watermark: "वॉटरमार्क",
  "Navigation buttons": "नेविगेशन बटन",
  "Pane buttons": "पैन बटन",
  "Drawings toolbar": "ड्रॉइंग टूलबार",
  Legend: "लीजेंड",
  "Price and percent": "कीमत और प्रतिशत",
  Scale: "स्केल",
  Always: "हमेशा",
  Never: "कभी नहीं",
  Apply: "लागू करें",
  "Chart properties": "चार्ट सेटिंग",
  "Chart settings": "चार्ट सेटिंग",
  Name: "नाम",
  Relative: "सापेक्ष",
  "Previous day close": "पिछले दिन का बंद",
  "Pre/post market": "प्री/पोस्ट मार्केट",
  "Values in status line": "स्टेटस लाइन में मान",
  "Last price": "अंतिम कीमत",
  Body: "बॉडी",
  Borders: "बॉर्डर",
  Wick: "विक",
  "Price scale": "मूल्य स्केल",
  "Time scale": "समय स्केल",
  "Grid lines": "ग्रिड रेखाएँ",
  "Show logo": "लोगो दिखाएँ",
  "Symbol description": "प्रतीक विवरण",
};

function chartSettingsLabelMap() {
  if (loadAppLanguageId() !== "hi") return null;
  return {
    Settings: t("settings", "hi"),
    "Chart Settings": t("chartSettings", "hi"),
    "Chart settings": t("chartSettings", "hi"),
    "Chart properties": t("chartSettings", "hi"),
    Symbol: t("symbol", "hi"),
    "Status line": t("statusLine", "hi"),
    "Scales and lines": t("scalesAndLines", "hi"),
    Canvas: t("canvas", "hi"),
    Cancel: t("cancel", "hi"),
    Ok: t("ok", "hi"),
    OK: t("ok", "hi"),
    Open: t("open", "hi"),
    High: t("high", "hi"),
    Low: t("low", "hi"),
    Close: t("close", "hi"),
    Volume: t("volume", "hi"),
    Time: t("time", "hi"),
    Bid: t("bid", "hi"),
    Ask: t("ask", "hi"),
    ...TV_CHART_HI_INNER,
  };
}

function applyMappedLabel(map, value) {
  if (value == null) return value;
  const raw = String(value);
  const trimmed = raw.trim();
  if (!trimmed) return value;
  const lookup = trimmed.replace(/:$/, "");
  const hi = map[trimmed] || map[lookup];
  if (!hi) return value;
  const colon = /:$/.test(trimmed) && !hi.endsWith(":") ? ":" : "";
  return raw.replace(trimmed, hi + colon);
}

function applyChartSettingsLanguage(dialog) {
  const map = chartSettingsLabelMap();
  if (!map || !dialog) return;
  const doc = dialog.ownerDocument || document;
  const walker = doc.createTreeWalker(dialog, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  for (const node of nodes) {
    const next = applyMappedLabel(map, node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
  }
  dialog.querySelectorAll?.("[aria-label], [title], [placeholder]").forEach((el) => {
    ["aria-label", "title", "placeholder"].forEach((attr) => {
      if (!el.hasAttribute(attr)) return;
      const next = applyMappedLabel(map, el.getAttribute(attr));
      if (next !== el.getAttribute(attr)) el.setAttribute(attr, next);
    });
  });
}

/**
 * Mobile only: open Chart Settings full-bleed on the category list first
 * (Symbol / Status line / Scales and lines / Canvas). Same TV content/actions;
 * desktop `executeAction("chartProperties")` is unchanged.
 *
 * TV already supports this when TabletSmall (≤440px): it shows a native Back
 * button and hides tabs while a page is open. We click that Back once on open.
 * When the chart iframe is wider (desktop layout on a phone), we simulate the
 * same list → detail flow with CSS on tabsContainer / tabContent.
 */
export function openChartPropertiesMobile({ onOpen, onClose } = {}) {
  if (typeof document === "undefined") return;

  const fireProperties = () => {
    try {
      const c = widget?.activeChart?.();
      if (typeof c?.executeActionById === "function") {
        c.executeActionById("chartProperties");
        return true;
      }
    } catch (err) {
      log('executeActionById("chartProperties") failed:', err);
    }
    executeAction("chartProperties");
    return false;
  };

  waitForActiveChart(12000).then((chart) => {
    if (chart) {
      try {
        chart.executeActionById("chartProperties");
      } catch (err) {
        log('executeActionById("chartProperties") failed:', err);
        fireProperties();
      }
    } else {
      fireProperties();
    }
  });

  const CLASS = "fx-chart-properties-open";
  const MOBILE_DIALOG = "fx-chart-settings-mobile";
  const LIST = "fx-chart-settings-list";
  const DETAIL = "fx-chart-settings-detail";
  const STYLE_ID = "fx-chart-settings-mobile-css";
  const TABS_SEL = ".tabsContainer-F0WBLDV5, [class*=\"tabsContainer-\"]";
  const CONTENT_SEL = ".tabContent-F0WBLDV5, [class*=\"tabContent-\"]";
  const BACK_SEL = ".backButton-yMMXpYEB, [class*=\"backButton-\"]";

  document.documentElement.classList.add(CLASS);

  let seen = false;
  let openedNotified = false;
  const notifyOpen = () => {
    if (openedNotified) return;
    openedNotified = true;
    onOpen?.();
  };
  let ticks = 0;
  const maxTicks = 250;
  /** Per-dialog: we only auto-click TV's native back once. */
  const primed = new WeakSet();
  const langObservers = [];

  const parentIsDark = () =>
    document.documentElement.classList.contains("fx-theme-dark") ||
    document.documentElement.classList.contains("theme-dark") ||
    !!document.querySelector(".trading-platform.dark");

  const MOBILE_CSS = `
.${MOBILE_DIALOG},
[role="dialog"].${MOBILE_DIALOG}{
  --fx-cs-bg:#fff;--fx-cs-text:#111;--fx-cs-muted:#6b7280;--fx-cs-line:#e5e7eb;--fx-cs-icon:#111;
  position:fixed!important;inset:0!important;width:100%!important;max-width:100%!important;
  height:100%!important;max-height:100%!important;min-width:0!important;margin:0!important;
  border-radius:0!important;box-sizing:border-box!important;display:flex!important;
  flex-direction:column!important;background:var(--fx-cs-bg)!important;color:var(--fx-cs-text)!important;
  z-index:2147483000!important;overflow:hidden!important;
}
html.theme-dark .${MOBILE_DIALOG},
html.fx-theme-dark .${MOBILE_DIALOG},
.${MOBILE_DIALOG}--dark,
[role="dialog"].${MOBILE_DIALOG}--dark{
  --fx-cs-bg:#131722;--fx-cs-text:#d1d4dc;--fx-cs-muted:#868993;--fx-cs-line:#2a2e39;--fx-cs-icon:#d1d4dc;
}
.${MOBILE_DIALOG} .content-F0WBLDV5{
  display:flex!important;flex:1 1 auto!important;flex-direction:column!important;
  min-height:0!important;width:100%!important;overflow:hidden!important;
  background:var(--fx-cs-bg)!important;
}
.${MOBILE_DIALOG} .container-nGEmjtaX{
  width:100%!important;max-width:100%!important;border-inline-end:none!important;
  padding:4px 0!important;background:var(--fx-cs-bg)!important;
}
.${MOBILE_DIALOG} [class*="header"],
.${MOBILE_DIALOG} [class*="Header"],
.${MOBILE_DIALOG} [class*="footer"],
.${MOBILE_DIALOG} [class*="Footer"]{
  background:var(--fx-cs-bg)!important;color:var(--fx-cs-muted)!important;
  border-color:var(--fx-cs-line)!important;
}
.${MOBILE_DIALOG} .tab-nGEmjtaX{
  display:flex!important;align-items:center!important;gap:14px!important;width:100%!important;
  min-height:52px!important;height:auto!important;padding:10px 16px!important;margin:0!important;
  border-radius:0!important;box-sizing:border-box!important;background:transparent!important;
  color:var(--fx-cs-text)!important;position:relative!important;
}
.${MOBILE_DIALOG} .tab-nGEmjtaX svg{
  color:var(--fx-cs-icon)!important;
}
.${MOBILE_DIALOG} .tab-nGEmjtaX svg [stroke]:not([stroke="none"]){stroke:currentColor!important}
.${MOBILE_DIALOG} .tab-nGEmjtaX svg [fill]:not([fill="none"]){fill:currentColor!important}
.${MOBILE_DIALOG} .tab-nGEmjtaX .title-nGEmjtaX{
  flex:1 1 auto!important;min-width:0!important;font-size:15px!important;font-weight:400!important;
  line-height:1.25!important;color:var(--fx-cs-text)!important;white-space:nowrap!important;overflow:hidden!important;
  text-overflow:ellipsis!important;word-break:normal!important;
}
.${MOBILE_DIALOG} .tab-nGEmjtaX::after{
  content:""!important;flex:0 0 auto!important;width:8px!important;height:8px!important;
  margin-left:8px!important;border-right:2px solid var(--fx-cs-muted)!important;
  border-bottom:2px solid var(--fx-cs-muted)!important;transform:rotate(-45deg)!important;
}
/* CSS drill-down when TV keeps desktop sidebar+content (iframe wider than 440px) */
.${MOBILE_DIALOG}.${LIST} .tabContent-F0WBLDV5,
.${MOBILE_DIALOG}.${LIST} [class*="tabContent-"]{display:none!important}
.${MOBILE_DIALOG}.${LIST} .tabsContainer-F0WBLDV5,
.${MOBILE_DIALOG}.${LIST} [class*="tabsContainer-"]{
  display:block!important;flex:1 1 auto!important;width:100%!important;max-width:100%!important;
  overflow-y:auto!important;-webkit-overflow-scrolling:touch!important;
}
.${MOBILE_DIALOG}.${DETAIL} .tabsContainer-F0WBLDV5,
.${MOBILE_DIALOG}.${DETAIL} [class*="tabsContainer-"]{display:none!important}
.${MOBILE_DIALOG}.${DETAIL} .tabContent-F0WBLDV5,
.${MOBILE_DIALOG}.${DETAIL} [class*="tabContent-"]{
  display:block!important;flex:1 1 auto!important;width:100%!important;min-height:0!important;
  overflow-x:hidden!important;overflow-y:auto!important;-webkit-overflow-scrolling:touch!important;
}
.fx-chart-settings-back{
  display:none!important;width:36px!important;height:36px!important;margin:0 4px 0 0!important;
  padding:0!important;border:none!important;background:transparent!important;cursor:pointer!important;
  position:relative!important;flex:0 0 auto!important;align-items:center!important;justify-content:center!important;
}
.fx-chart-settings-back::before{
  content:""!important;display:block!important;width:10px!important;height:10px!important;
  border-left:2px solid var(--fx-cs-icon,#111)!important;border-bottom:2px solid var(--fx-cs-icon,#111)!important;
  transform:rotate(45deg)!important;margin-left:4px!important;
}
.${MOBILE_DIALOG}.${DETAIL} .fx-chart-settings-back{display:inline-flex!important}
.${MOBILE_DIALOG} .content-RLntasnw{
  display:flex!important;flex-direction:column!important;width:100%!important;max-width:100%!important;
  padding:8px 16px 28px!important;box-sizing:border-box!important;grid-template-columns:none!important;
}
.${MOBILE_DIALOG} .cell-RLntasnw{
  display:block!important;width:100%!important;max-width:100%!important;min-width:0!important;
  grid-column:1/-1!important;box-sizing:border-box!important;
}
.${MOBILE_DIALOG} .cell-RLntasnw.first-RLntasnw .inner-RLntasnw,
.${MOBILE_DIALOG} .title-FG0u1J5p{
  white-space:normal!important;word-break:normal!important;overflow-wrap:break-word!important;
  writing-mode:horizontal-tb!important;max-width:100%!important;font-size:14px!important;
  line-height:1.4!important;color:var(--fx-cs-text)!important;
}
.${MOBILE_DIALOG} [class*="close"] svg,
.${MOBILE_DIALOG} [aria-label="Close"] svg,
.${MOBILE_DIALOG} [class*="header"] svg{
  color:var(--fx-cs-icon)!important;
}
`;

  const visitDialogDocs = (fn) => {
    const frames = Array.from(document.querySelectorAll("iframe"));
    for (const frame of frames) {
      try {
        const doc = frame.contentDocument;
        if (doc) fn(doc);
      } catch {
        /* cross-origin */
      }
    }
    fn(document);
  };

  const ensureMobileCss = (doc) => {
    if (!doc?.head) return;
    let style = doc.getElementById(STYLE_ID);
    if (!style) {
      style = doc.createElement("style");
      style.id = STYLE_ID;
      doc.head.appendChild(style);
    }
    style.textContent = MOBILE_CSS;
  };

  const queryDialogs = (doc) => {
    const roots = new Set();
    doc
      .querySelectorAll?.(
        '[data-name="series-properties-dialog"], [data-name="chart-properties-dialog"]'
      )
      ?.forEach((el) => {
        roots.add(el.closest?.('[role="dialog"]') || el);
      });
    if (roots.size) return Array.from(roots);
    return Array.from(doc.querySelectorAll?.('[role="dialog"]') || []).filter((el) =>
      el.querySelector?.(
        `${TABS_SEL}, ${CONTENT_SEL}, ${BACK_SEL}, .container-nGEmjtaX, .content-F0WBLDV5`
      )
    );
  };

  const findDialogOpen = () => {
    let found = false;
    visitDialogDocs((doc) => {
      if (queryDialogs(doc).length) found = true;
    });
    return found;
  };

  const showList = (dialog) => {
    dialog.classList.add(LIST);
    dialog.classList.remove(DETAIL);
  };

  const showDetail = (dialog) => {
    dialog.classList.remove(LIST);
    dialog.classList.add(DETAIL);
  };

  const ensureFallbackBack = (doc, dialog) => {
    let back = dialog.querySelector("#fx-chart-settings-back");
    if (back) {
      back.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        showList(dialog);
      };
      return;
    }
    const header =
      dialog.querySelector?.('[class*="header"]') ||
      dialog.querySelector?.('[class*="Header"]') ||
      dialog;
    back = doc.createElement("button");
    back.id = "fx-chart-settings-back";
    back.type = "button";
    back.setAttribute("aria-label", "Back to settings list");
    back.className = "fx-chart-settings-back";
    back.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      showList(dialog);
    });
    header.insertBefore(back, header.firstChild);
  };

  const wireFallbackNav = (doc, dialog) => {
    if (dialog.dataset.fxChartSettingsNav === "1") return;
    dialog.dataset.fxChartSettingsNav = "1";
    ensureFallbackBack(doc, dialog);

    const onTabActivate = () => {
      // TV selects the page first; then we switch to detail chrome.
      requestAnimationFrame(() => showDetail(dialog));
    };

    dialog.querySelectorAll?.(TABS_SEL).forEach((tabs) => {
      tabs.addEventListener("click", onTabActivate, true);
    });
    dialog.querySelectorAll?.(".tab-nGEmjtaX, [class*='tab-nGEmjtaX']").forEach((tab) => {
      tab.addEventListener("click", onTabActivate, true);
    });
  };

  const watchChartSettingsLanguage = (dialog) => {
    applyChartSettingsLanguage(dialog);
    if (dialog.dataset.fxChartLang === "1") return;
    dialog.dataset.fxChartLang = "1";
    const obs = new MutationObserver(() => applyChartSettingsLanguage(dialog));
    obs.observe(dialog, { childList: true, subtree: true, characterData: true });
    langObservers.push(obs);
  };

  const stopChartSettingsLanguage = () => {
    while (langObservers.length) {
      try {
        langObservers.pop().disconnect();
      } catch {
        /* ignore */
      }
    }
  };

  const enhanceMobileDialog = () => {
    visitDialogDocs((doc) => {
      ensureMobileCss(doc);

      queryDialogs(doc).forEach((dialog) => {
        dialog.classList.add(MOBILE_DIALOG);
        const dark =
          parentIsDark() ||
          doc.documentElement.classList.contains("theme-dark") ||
          doc.documentElement.classList.contains("fx-theme-dark") ||
          doc.body?.classList.contains("theme-dark");
        dialog.classList.toggle(`${MOBILE_DIALOG}--dark`, !!dark);
        watchChartSettingsLanguage(dialog);

        const nativeBack = dialog.querySelector?.(BACK_SEL);
        const hasTabs = !!dialog.querySelector?.(TABS_SEL);
        const hasContent = !!dialog.querySelector?.(CONTENT_SEL);

        // Native TV mobile detail (≤440px): Back clears activePage → category list.
        if (nativeBack && !primed.has(dialog)) {
          primed.add(dialog);
          try {
            nativeBack.click();
          } catch (err) {
            log("chart settings native back click failed:", err);
          }
          return;
        }

        // Native TV list (tabs only, no page content) — leave TV alone.
        if (hasTabs && !hasContent) {
          dialog.classList.remove(LIST, DETAIL);
          return;
        }

        // Content-only while Back is still mounting — wait for next tick.
        if (hasContent && !hasTabs && !nativeBack) {
          return;
        }

        // Desktop-in-iframe layout: both sidebar + Symbol content → CSS list-first.
        if (hasTabs && hasContent) {
          if (!dialog.classList.contains(DETAIL)) showList(dialog);
          wireFallbackNav(doc, dialog);
          dialog.querySelectorAll?.(".container-nGEmjtaX").forEach((el) => {
            el.classList.add("isMobile-nGEmjtaX");
            el.classList.remove("isTablet-nGEmjtaX");
          });
        }
      });
    });
  };

  enhanceMobileDialog();
  const timer = setInterval(() => {
    ticks += 1;
    const open = findDialogOpen();
    if (!open && ticks % 5 === 0) fireProperties();
    if (open) {
      enhanceMobileDialog();
      notifyOpen();
      seen = true;
    }
    if ((seen && !open) || ticks >= maxTicks) {
      clearInterval(timer);
      if (seen && !open) stopChartSettingsLanguage();
      document.documentElement.classList.remove(CLASS);
      onClose?.();
    }
  }, 100);
}

/**
 * Zoom via time-scale bar spacing.
 * Note: `zoomIn` / `zoomOut` are NOT ChartActionId values in this library build —
 * only `chart.zoomOut()` exists as a dedicated method.
 */
export function zoomIn(factor = 1.25) {
  const chart = activeChart();
  if (!chart) {
    log("zoomIn: chart not ready");
    return false;
  }
  try {
    const ts = chart.getTimeScale?.();
    if (ts && typeof ts.barSpacing === "function" && typeof ts.setBarSpacing === "function") {
      const cur = Number(ts.barSpacing()) || 6;
      ts.setBarSpacing(Math.min(cur * factor, 64));
      return true;
    }
    if (typeof chart.getVisibleRange === "function" && typeof chart.setVisibleRange === "function") {
      const range = chart.getVisibleRange();
      if (!range || range.to <= range.from) return false;
      const span = range.to - range.from;
      const mid = (range.from + range.to) / 2;
      const half = (span / factor) / 2;
      chart.setVisibleRange({ from: mid - half, to: mid + half });
      return true;
    }
    log("zoomIn: no timeScale / visibleRange API");
    return false;
  } catch (err) {
    log("zoomIn failed:", err);
    return false;
  }
}

export function zoomOut(factor = 1.25) {
  const chart = activeChart();
  if (!chart) {
    log("zoomOut: chart not ready");
    return false;
  }
  try {
    if (typeof chart.zoomOut === "function") {
      try {
        if (typeof chart.canZoomOut !== "function" || chart.canZoomOut()) {
          chart.zoomOut();
          return true;
        }
      } catch {
        /* fall through to barSpacing */
      }
    }
    const ts = chart.getTimeScale?.();
    if (ts && typeof ts.barSpacing === "function" && typeof ts.setBarSpacing === "function") {
      const cur = Number(ts.barSpacing()) || 6;
      ts.setBarSpacing(Math.max(cur / factor, 0.5));
      return true;
    }
    if (typeof chart.getVisibleRange === "function" && typeof chart.setVisibleRange === "function") {
      const range = chart.getVisibleRange();
      if (!range || range.to <= range.from) return false;
      const span = range.to - range.from;
      const mid = (range.from + range.to) / 2;
      const half = (span * factor) / 2;
      chart.setVisibleRange({ from: mid - half, to: mid + half });
      return true;
    }
    log("zoomOut: no zoom API available");
    return false;
  } catch (err) {
    log("zoomOut failed:", err);
    return false;
  }
}

/**
 * MT5-style volume modes on the TradingView chart:
 * - volumes: Volume study overlaid on the price pane
 * - tick: Volume study in its own pane (closest TV equivalent of Tick Volumes)
 * - none: remove Volume studies
 */
export async function getVolumeMode() {
  try {
    const chart = activeChart();
    if (!chart?.getAllStudies) return "none";
    const studies = await chart.getAllStudies();
    const vol = (studies || []).find((s) => String(s?.name || "").toLowerCase() === "volume");
    if (!vol) return "none";
    // Library does not expose overlay flag reliably — prefer preference / last set mode.
    return _volumeModeCache || "volumes";
  } catch (err) {
    log("getVolumeMode failed:", err);
    return "none";
  }
}

let _volumeModeCache = null;

export async function setVolumeMode(mode) {
  const chart = activeChart();
  if (!chart) {
    log("setVolumeMode: chart not ready");
    return false;
  }
  try {
    if (typeof chart.getAllStudies === "function") {
      const studies = await chart.getAllStudies();
      for (const s of studies || []) {
        if (String(s?.name || "").toLowerCase() === "volume" && s.id != null) {
          try {
            chart.removeEntity(s.id);
          } catch {
            /* ignore */
          }
        }
      }
    }
    if (mode === "none") {
      _volumeModeCache = "none";
      return true;
    }
    if (typeof chart.createStudy !== "function") {
      log("setVolumeMode: createStudy unavailable");
      return false;
    }
    // forceOverlay=true → Volumes on price pane; false → separate pane (Tick Volumes)
    const forceOverlay = mode === "volumes";
    await chart.createStudy("Volume", forceOverlay, false);
    _volumeModeCache = mode === "tick" ? "tick" : "volumes";
    return true;
  } catch (err) {
    log(`setVolumeMode("${mode}") failed:`, err);
    return false;
  }
}

/**
 * Capture the chart as a PNG and download it locally.
 * `widget.takeScreenshot()` only uploads to `snapshot_url` (not configured here),
 * so we use `takeClientScreenshot()` + a local download link instead.
 */
export async function takeScreenshot() {
  try {
    if (!widget) {
      log("takeScreenshot: chart widget not ready");
      return;
    }
    if (typeof widget.takeClientScreenshot !== "function") {
      log("takeScreenshot: takeClientScreenshot unavailable");
      return;
    }

    const canvas = await widget.takeClientScreenshot();
    if (!canvas) {
      log("takeScreenshot: empty canvas");
      return;
    }

    let symbolPart = "chart";
    try {
      const sym =
        widget.activeChart?.()?.symbol?.() ||
        widget.symbol?.() ||
        "";
      if (sym) symbolPart = String(sym).replace(/[^\w.-]+/g, "_").slice(0, 48);
    } catch {
      /* ignore */
    }

    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const filename = `${symbolPart}-${stamp}.png`;

    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/png");
      } catch {
        resolve(null);
      }
    });

    const href = blob
      ? URL.createObjectURL(blob)
      : canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.download = filename;
    link.href = href;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();

    if (blob) {
      setTimeout(() => {
        try {
          URL.revokeObjectURL(href);
        } catch {
          /* ignore */
        }
      }, 2500);
    }
  } catch (err) {
    log("takeScreenshot failed:", err);
  }
}

// Widget-level toggles backed by IWatchedValue (read / write / subscribe).
const TOGGLES = {
  magnet: "magnetEnabled",
  hide: "hideAllDrawingTools",
  lock: "lockAllDrawingTools",
};

function watched(key) {
  const name = TOGGLES[key];
  if (!name) return null;
  try {
    return widget?.[name]?.() ?? null;
  } catch {
    return null;
  }
}

export function getToggle(key) {
  try { return !!watched(key)?.value(); } catch { return false; }
}

export function toggle(key) {
  const wv = watched(key);
  if (!wv) {
    log(`toggle "${key}" unavailable.`);
    return false;
  }
  try {
    const next = !wv.value();
    wv.setValue(next);
    return next;
  } catch (err) {
    log(`toggle "${key}" failed:`, err);
    return false;
  }
}
